import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Box,
  Typography,
  IconButton,
  Divider,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { clear_notification, delete_notification } from "redux/actions/AppActions";
import ClearIcon from "@mui/icons-material/Clear";
import ClearAllRoundedIcon from "@mui/icons-material/ClearAllRounded";
import dayjs from "dayjs";
import { epochConverter } from "config/helpers/date_helper";
import { mappedLanguages } from "config/helpers/common_helper";
import generateLabel from "../../config/helpers/syncLabel";
import { useActiveSyncTasks } from "../../hooks/useGlobalSync";

const NotificationModal = ({ open, onClose }) => {
  const app = useSelector((state) => state?.app);
  const notificationMessage = app?.notification;
  const reduxDispatch = useDispatch();
  const { i18n } = useTranslation();

  // Use hook to get active tasks instead of manual polling
  const activeTasksData = useActiveSyncTasks();

  // Keep single latest sync-complete notification
  const notifications = useMemo(() => {
    const raw = Array.isArray(notificationMessage) ? notificationMessage : [];
    const syncComplete = raw.filter((n) => n?.type === "sync-complete");
    if (syncComplete.length <= 1) return raw;
    const latest = syncComplete.reduce((a, b) =>
      (a?.addedOn || 0) > (b?.addedOn || 0) ? a : b
    );
    const others = raw.filter((n) => n?.type !== "sync-complete");
    return [...others, latest];
  }, [notificationMessage]);

  // Merge notifications + poller data
  const { activeSyncGroup, completedSyncGroups, otherNotifications } =
    useMemo(() => {
      const syncTypes = new Set(["sync-progress", "sync-complete"]);
      const syncNotifications = (notifications || []).filter((n) =>
        syncTypes.has(n?.type)
      );
      const otherNotificationsLocal = (notifications || []).filter(
        (n) => !syncTypes.has(n?.type)
      );
      const grouped = {};

      syncNotifications.forEach((n) => {
        const id = n?.id || n?.message || "__unknown__";
        grouped[id] = { ...(grouped[id] || {}), ...n };
        if (grouped[id]?.type === "sync-complete") {
          grouped[id].label = grouped[id].label || "Task Sync";
          grouped[id].status = grouped[id].status || "COMPLETED";
          grouped[id].progress =
            typeof grouped[id].progress === "number" ? grouped[id].progress : 100;
          if (!grouped[id].addedOn) grouped[id].addedOn = Date.now();
        } else {
          grouped[id].label = grouped[id].label || null;
        }
      });

      (activeTasksData || []).forEach((t) => {
        const id = `${t.id}-sync`;
        const friendly = t.label ? generateLabel(t.label) : null;
        if (!grouped[id]) {
          grouped[id] = {
            id,
            progress: typeof t.progress === "number" ? t.progress : 0,
            status: t.status || "RUNNING",
            addedOn: Date.now(),
            type: "sync-progress",
            label: friendly,
            processedGroups: Array.isArray(t.processedGroups)
              ? t.processedGroups
              : null,
            subtaskCode: t.subtaskCode || null,
            subtaskProgress:
              typeof t.subtaskProgress === "number"
                ? t.subtaskProgress
                : null,
          };
        } else {
          if (friendly) grouped[id].label = friendly;
          grouped[id].progress =
            typeof t.progress === "number" ? t.progress : grouped[id].progress || 0;
          grouped[id].status = t.status || grouped[id].status || "RUNNING";
          if (t.subtaskCode) grouped[id].subtaskCode = t.subtaskCode;
          if (typeof t.subtaskProgress === "number")
            grouped[id].subtaskProgress = t.subtaskProgress;
          if (t.processedGroups) {
            grouped[id].processedGroups = Array.isArray(t.processedGroups)
              ? t.processedGroups
              : [];
          }
        }
      });

      const groups = Object.keys(grouped).map((id) => ({
        id,
        ...grouped[id],
      }));

      // Separate active (running) and completed sync groups
      const active = groups.find(
        (g) =>
          (g.status || "").toUpperCase() === "RUNNING" ||
          (g.progress || 0) < 100
      ) || null;

      const completed = groups.filter(
        (g) =>
          (g.status || "").toUpperCase() === "COMPLETED" ||
          (g.progress || 0) === 100
      );

      // Find the current active group from processedGroups if available
      let activeGroup = null;
      if (active && Array.isArray(active.processedGroups)) {
        const pgIndex = active.processedGroups.findIndex(
          (pg) => (pg.progress || 0) < 100
        );
        activeGroup =
          pgIndex >= 0
            ? active.processedGroups[pgIndex]
            : active.processedGroups[0] || null;
      }

      return {
        activeSyncGroup: activeGroup,
        hasRunningSync: active !== null,
        completedSyncGroups: completed,
        otherNotifications: otherNotificationsLocal,
      };
    }, [notifications, activeTasksData]);

  const handleClearAllNotifications = () => reduxDispatch(clear_notification());
  const handleDeleteNotification = (data) =>
    reduxDispatch(delete_notification(data));

  const effectiveOpen = Boolean(open);

  return (
    <Dialog open={effectiveOpen} onClose={onClose}>
      <Box
        sx={{
          position: "fixed",
          top: "64px",
          right: "2rem",
        }}
        className="notification-height"
      >
        <Paper sx={{ width: "360px", minHeight: "200px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <DialogTitle sx={{ paddingLeft: "14px" }}>
              {i18n.t("notifications.NOTIFICATION")}
            </DialogTitle>
            <Tooltip
              enterTouchDelay={0}
              disableInteractive
              placement="left"
              title={i18n.t("common.CLEAR_ALL")}
            >
              <IconButton
                sx={{
                  height: "50px",
                  width: "50px",
                  borderRadius: "50%",
                  marginRight: "10px",
                }}
                onClick={handleClearAllNotifications}
              >
                <ClearAllRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              maxHeight: "40vh",
              overflowY: "auto",
              overflowX: "visible",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {activeSyncGroup || completedSyncGroups?.length || otherNotifications?.length ? (
              <>
                {/* Active Sync (Pinned) */}
                {activeSyncGroup && (
                  <Box sx={{ p: 2, background: "background.paper", position: "sticky", top: 0, zIndex: 10 }}>
                    {/* Currently Syncing Label */}
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        mb: 0.75,
                      }}
                    >
                      Currently Syncing
                    </Typography>

                    {/* Task Group Name */}
                    <Tooltip title={activeSyncGroup.label || activeSyncGroup.code}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          mb: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {activeSyncGroup.label || activeSyncGroup.code}
                      </Typography>
                    </Tooltip>

                    {/* Progress Bar with Percentage */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={activeSyncGroup.progress || 0}
                          sx={{ height: 8, borderRadius: 2 }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "primary.main",
                          fontWeight: 600,
                          minWidth: 40,
                          textAlign: "right",
                        }}
                      >
                        {activeSyncGroup.progress || 0}%
                      </Typography>
                    </Box>

                    {/* Task Status Text */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {activeSyncGroup.status === "COMPLETED"
                        ? "Completed"
                        : activeSyncGroup.status === "RUNNING"
                          ? (() => {
                            const sub = (activeSyncGroup.tasks || []).find(
                              (tsk) => tsk && tsk.friendlySubtask
                            );
                            const label = sub
                              ? sub.friendlySubtask
                              : activeSyncGroup.label;
                            return `Syncing: ${label} (${activeSyncGroup.progress}%)`;
                          })()
                          : activeSyncGroup.status === "FAILED"
                            ? "Failed"
                            : "Pending"}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                  </Box>
                )}

                {/* Completed Sync Notifications */}
                {completedSyncGroups.map((group) => (
                  <React.Fragment key={group?.id}>
                    <Box
                      sx={{
                        padding: "8px 10px",
                        margin: "4px",
                        background: !group?.isRead
                          ? app?.theme === "light"
                            ? "#dfe9f5"
                            : app?.theme === "dark"
                              ? "#5c5d5e"
                              : "inherit"
                          : "inherit",
                        borderRadius: "5px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0,
                        }}
                      >
                        <Typography
                          fontSize={14}
                          sx={{ fontFamily: "Poppins", fontWeight: "500" }}
                        >
                          Data Sync Completed.
                          <Typography
                            fontSize={10}
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: "400",
                            }}
                          >
                            {dayjs(epochConverter(group?.addedOn)).format(
                              mappedLanguages?.[i18n?.language]?.timeWithDateFormat
                            )}
                          </Typography>
                        </Typography>
                        <IconButton
                          onClick={() =>
                            handleDeleteNotification(group?.addedOn)
                          }
                        >
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Divider variant="middle" />
                  </React.Fragment>
                ))}

                {/* Other Notifications */}
                {otherNotifications.map((notification) => (
                  <React.Fragment
                    key={notification?.addedOn || notification?.id || notification?.message}
                  >
                    <Box
                      sx={{
                        padding: "8px 10px",
                        margin: "4px",
                        background: !notification?.isRead
                          ? app?.theme === "light"
                            ? "#dfe9f5"
                            : app?.theme === "dark"
                              ? "#5c5d5e"
                              : "inherit"
                          : "inherit",
                        borderRadius: "5px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0,
                        }}
                      >
                        <Typography
                          fontSize={14}
                          sx={{ fontFamily: "Poppins", fontWeight: "500" }}
                        >
                          {notification?.message}
                          <Typography
                            fontSize={10}
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: "400",
                            }}
                          >
                            {dayjs(epochConverter(notification?.addedOn)).format(
                              mappedLanguages?.[i18n?.language]?.timeWithDateFormat
                            )}
                          </Typography>
                        </Typography>
                        <IconButton
                          onClick={() =>
                            handleDeleteNotification(notification?.addedOn)
                          }
                        >
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Divider variant="middle" />
                  </React.Fragment>
                ))}
              </>
            ) : (
              <DialogContent sx={{ fontFamily: "Poppins", fontWeight: "500" }}>
                {i18n.t("notifications.NO_NOTIFICATIONS_RIGHT_NOW")}
              </DialogContent>
            )}
          </Box>

          <DialogActions>
            <Button onClick={onClose} color="primary">
              {i18n.t("notifications.CLOSE")}
            </Button>
          </DialogActions>
        </Paper>
      </Box>
    </Dialog>
  );
};

export default NotificationModal;